/**
 * asyncMessageHelper for using IDI/Lexa service
 * Lexa API confluence page: https://ppaas/api/3719176155268900#apiReference
 * Async Message Availability types: ['AVAILABLE', 'DELAYED_AVAILABLE']; non-eligibility will be take care of inside Lexa team
 */
import _find from 'lodash/find';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { parse, toSeconds } from 'iso8601-duration';
import poll from './poll';
import { asyncChatValidator } from './node-chat-on-page-validator';
import { unreadConversationChecker } from './conversationMsgHelper';

//  The IDI channel mapping base on their response; parse IDI option as SAC options
const IDIChannelMapping = {
  EMAIL: 'email',
  PHONE: 'callus',
  COMMUNITY: 'community',
  RES_CENTER: 'seller',
  CHAT: 'chat',
  MESSAGE_US: 'message',
};

// IDI channels filter
const IDIChannelFilter = (channels) => {
  if (!Array.isArray(channels) || channels.length <= 0) {
    return null;
  }
  const mapping = IDIChannelMapping;

  // Contact opions parser and validation
  return channels.map(channel => (channel.name && mapping[channel.name] && channel.rank ? {
    type: mapping[channel.name],
    priority: `${channel.rank}`,
  } : {}));
};

// Return Async Message estimate wait time in minute
// Duration confluence page: https://en.wikipedia.org/wiki/ISO_8601#Durations
const getEstimateWaitTime = (waitPeriod) => {
  if (typeof waitPeriod !== 'string' || !waitPeriod) {
    return null;
  }

  const second = toSeconds(parse(waitPeriod));
  return Math.ceil(second / 60);
};

// Aync message contact option validation
const hasAyncMessage = channels => Array.isArray(channels) && channels.some(option => option.type === 'message');

// Get Message us contact option availability message
const getMessageAvailable = availabilities => _find(availabilities, availability => availability.name === 'MESSAGE_US');

const eligibilityList = ['AVAILABLE', 'DELAYED_AVAILABLE'];

// checking Async message whether available or not (base on the channel_availability_decisions data)
const isAvailableDecision = (availabilities) => {
  const messageUs = getMessageAvailable(availabilities);
  return !_isEmpty(messageUs) && eligibilityList.indexOf(messageUs.availability_decision) >= 0;
};

const isAsyncChatEnable = ({ channels, availability }) => hasAyncMessage(channels) && isAvailableDecision(availability);

// Get Estimate wait time for Asyc message
const getWaitTime = (availabilities) => {
  const messageUs = getMessageAvailable(availabilities);
  return getEstimateWaitTime(_get(messageUs, 'wait_time_to_availability.wait_period'));
};

// Redirect to SMC, if there is unread conversation
const redirectSMC = () => window.open('/smc/customer?isActive=true', '_self');

// Directly open Async chat, if there is no unread conversation/initialize conversation
// Setting topicRefId as GENPOOL, if there is no topic select intent
const openAsyncChat = ({ topicRefId = 'GENPOOL' }) => {
  const params = { sessionType: 'conversation', hasReply: true, isExtended: false };
  if (topicRefId) {
    params.topicName = topicRefId;
  }
  window.nodeChat.showAsync('', params);
};

// Checking and validate nodeChat async function
const invokingAsyncChat = params =>
  poll(asyncChatValidator, 10, 200)
    .then(() => openAsyncChat(params))
    .catch(() => {});

// Redirect promise direction switcher
const rediretPromise = (promise, params = {}) => {
  if (typeof promise !== 'function') {
    return null;
  }

  return promise().then((response) => {
    // Redirect to SMC if the MMT service have unread conversation
    if (response.available) {
      return redirectSMC();
    }
    return invokingAsyncChat(params);
  }).catch(() => redirectSMC()); // redirect to SMC if the MMT service failed
};

const initiateAsyncChat = (topicRefId) => {
  const parsedTopicRefId = !_isEmpty(topicRefId) ? `${topicRefId}|finalIntent` : 'GENPOOL|contactUsPage';
  return rediretPromise(unreadConversationChecker, { topicRefId: parsedTopicRefId });
};

module.exports = {
  IDIChannelFilter,
  hasAyncMessage,
  getEstimateWaitTime,
  isAsyncChatEnable,
  isAvailableDecision,
  getWaitTime,
  redirectSMC,
  openAsyncChat,
  rediretPromise,
  initiateAsyncChat,
};
